/**
 * Program Type Helpers — Shared utilities for course program types.
 *
 * Centralizes the mapping between database `program_type` values and
 * display labels, plus normalization and grouping helpers.
 *
 * Database values (per CHECK constraint):
 *   REGULAR, HOLIDAY, BOOTCAMP, CORPORATE
 *
 * NOTE: MASTERCLASS is NOT a Course program type. Masterclasses are
 * managed by a separate, independent Masterclass system.
 *
 * The UI displays friendlier labels (e.g. "Regular Programs").
 * Adding a new program type only requires adding it to PROGRAM_TYPE_LABELS
 * and PROGRAM_TYPE_ORDER below — no component changes needed.
 */

// Display labels keyed by DB value
export const PROGRAM_TYPE_LABELS = {
  REGULAR: 'Regular Programs',
  HOLIDAY: 'Holiday Programs',
  BOOTCAMP: 'Bootcamps',
  CORPORATE: 'Corporate',
}

// Display order (REGULAR first as the default/most-common tab)
export const PROGRAM_TYPE_ORDER = ['REGULAR', 'HOLIDAY', 'BOOTCAMP', 'CORPORATE']

// Short labels (used in compact UI like badges)
export const PROGRAM_TYPE_SHORT = {
  REGULAR: 'Regular',
  HOLIDAY: 'Holiday',
  BOOTCAMP: 'Bootcamp',
  CORPORATE: 'Corporate',
}

// Badge colors per program type (existing SMA palette)
export const PROGRAM_TYPE_BADGE = {
  REGULAR: 'bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-cinema-soft',
  HOLIDAY: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  BOOTCAMP: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  CORPORATE: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
}

// URL query param values — lowercase short forms for clean URLs
const URL_PARAM_TO_DB = {
  regular: 'REGULAR',
  holiday: 'HOLIDAY',
  bootcamp: 'BOOTCAMP',
  corporate: 'CORPORATE',
}

const DB_TO_URL_PARAM = {
  REGULAR: 'regular',
  HOLIDAY: 'holiday',
  BOOTCAMP: 'bootcamp',
  CORPORATE: 'corporate',
}

/**
 * Normalize a program_type value to a known DB value.
 * Falls back to 'REGULAR' for null/undefined/empty/unknown values.
 * @param {string|null|undefined} value
 * @returns {string} one of PROGRAM_TYPE_ORDER
 */
export function normalizeProgramType(value) {
  if (!value) return 'REGULAR'
  const upper = String(value).toUpperCase()
  if (PROGRAM_TYPE_LABELS[upper]) return upper
  // Try lowercase → DB mapping (e.g. url param "holiday" → "HOLIDAY")
  const fromUrl = URL_PARAM_TO_DB[String(value).toLowerCase()]
  if (fromUrl) return fromUrl
  return 'REGULAR'
}

/**
 * Get the display label for a program type.
 * @param {string} value
 * @returns {string}
 */
export function getProgramTypeLabel(value) {
  const normalized = normalizeProgramType(value)
  return PROGRAM_TYPE_LABELS[normalized] || normalized
}

/**
 * Convert a DB program_type to a URL-safe query param value.
 * @param {string} dbValue
 * @returns {string}
 */
export function programTypeToUrlParam(dbValue) {
  const normalized = normalizeProgramType(dbValue)
  return DB_TO_URL_PARAM[normalized] || 'regular'
}

/**
 * Convert a URL query param value back to a DB program_type.
 * Returns null if the param is not a recognized program type.
 * @param {string} paramValue
 * @returns {string|null}
 */
export function urlParamToProgramType(paramValue) {
  if (!paramValue) return null
  const db = URL_PARAM_TO_DB[String(paramValue).toLowerCase()]
  return db || null
}

/**
 * Group courses by program_type.
 * Courses with missing/null program_type are grouped under REGULAR.
 * @param {Array} courses
 * @returns {Object<string, Array>} keyed by DB program_type
 */
export function groupCoursesByProgramType(courses) {
  const groups = {}
  for (const type of PROGRAM_TYPE_ORDER) groups[type] = []
  for (const course of courses) {
    const type = normalizeProgramType(course.program_type)
    if (!groups[type]) groups[type] = []
    groups[type].push(course)
  }
  return groups
}

/**
 * Get course counts per program type, in display order.
 * Always returns an entry for every known program type (0 if none).
 * @param {Array} courses
 * @returns {Array<{value: string, label: string, count: number}>}
 */
export function getProgramTypeCounts(courses) {
  const groups = groupCoursesByProgramType(courses)
  return PROGRAM_TYPE_ORDER.map((value) => ({
    value,
    label: PROGRAM_TYPE_LABELS[value],
    count: (groups[value] || []).length,
  }))
}

/**
 * Filter courses to a single program type.
 * @param {Array} courses
 * @param {string} programType
 * @returns {Array}
 */
export function getCoursesByProgramType(courses, programType) {
  const normalized = normalizeProgramType(programType)
  return courses.filter((c) => normalizeProgramType(c.program_type) === normalized)
}
